import * as THREE from 'three';
import { buildCharacter, type BuiltCharacter } from '../character/builder';
import type { ARQrParams } from '../qr/types';

/**
 * AR モードでのキャラクター基準スケール。
 * 8th Wall の hit-test 座標系は実世界メートルなので、
 * GLB 単位 (3〜5) に対し 0.15 を掛けると現実 30〜75cm 程度になる。
 */
const AR_BASE_SCALE = 0.15;
const AR_SCALE_MIN = 0.05;
const AR_SCALE_MAX = 0.6;

/** 1本指タップ vs ドラッグの判定閾値（px） */
const TAP_MOVE_THRESHOLD = 10;

export type ArMode = 'searching' | 'placed';

export type ArHandles = {
  reset: () => void;
  getMode: () => ArMode;
};

export type CharacterPipelineModule = {
  name: string;
  onStart?: (args: { canvas: HTMLCanvasElement }) => void | Promise<void>;
  onUpdate?: () => void;
  onDetach?: () => void;
};

/**
 * 8th Wall のカスタム pipeline モジュール。
 *  - onStart  : シーンに照明とレチクル追加、GLB 読み込み、タッチハンドラ登録
 *  - onUpdate : 平面 hit-test → レチクル位置更新 / mixer 更新
 *  - onDetach : リソース破棄
 */
/** Shadow catcher / shadow map のチューニング値 */
const SHADOW_MAP_SIZE = 512;
const SHADOW_OPACITY = 0.4;
const SHADOW_CATCHER_SIZE = 4; // m, キャラ足元に敷く正方形プレーン
const SHADOW_CAMERA_HALF = 1.5; // m, ライトの正射影シャドウカメラ範囲

export function createCharacterPipelineModule(opts: {
  params: ARQrParams;
  onModeChange?: (mode: ArMode) => void;
}): { module: CharacterPipelineModule; handles: ArHandles } {
  let character: BuiltCharacter | null = null;
  let reticle: THREE.Mesh | null = null;
  let shadowCatcher: THREE.Mesh | null = null;
  let dirLight: THREE.DirectionalLight | null = null;
  let scene: THREE.Scene | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let mode: ArMode = 'searching';
  const clock = new THREE.Clock();

  // タッチ状態
  let touchKind: 'none' | 'tap' | 'drag' | 'pinch' = 'none';
  let tapStart = { x: 0, y: 0, t: 0 };
  let dragStartRotY = 0;
  let pinchStartDist = 0;
  let pinchStartScale = AR_BASE_SCALE;

  // 診断用カウンタ
  let frameCount = 0;
  let hitCount = 0;

  // ダブルタップ検出
  const DOUBLE_TAP_INTERVAL_MS = 320;
  const DOUBLE_TAP_DIST_PX = 30;
  let lastTapTime = 0;
  let lastTapPos = { x: 0, y: 0 };

  function setMode(m: ArMode) {
    if (mode === m) return;
    mode = m;
    opts.onModeChange?.(m);
  }

  function getXR8(): unknown {
    return (window as unknown as { XR8?: unknown }).XR8;
  }

  // 8th Wall API は型定義がないので any キャスト用ヘルパ
  function xr(): any {
    return getXR8();
  }

  function hitAt(xNorm: number, yNorm: number) {
    // 8th Wall の hitTest は配列を直接返す（{results: [...]} ではない）
    const hits = xr().XrController.hitTest(xNorm, yNorm, [
      'FEATURE_POINT',
      'ESTIMATED_SURFACE',
    ]);
    return Array.isArray(hits) && hits.length > 0 ? hits[0] : null;
  }

  function syncShadowCatcher() {
    if (!shadowCatcher || !character) return;
    shadowCatcher.position.copy(character.root.position);
    // ライトターゲットもキャラ足元に追従させ、shadow camera が常に被写体を捉える
    if (dirLight) {
      dirLight.target.position.copy(character.root.position);
      dirLight.target.updateMatrixWorld();
    }
  }

  function placeCharacterAt(clientX: number, clientY: number) {
    if (!character || !scene || !canvas) return;
    const xNorm = clientX / canvas.clientWidth;
    const yNorm = clientY / canvas.clientHeight;
    const hit = hitAt(xNorm, yNorm);
    if (!hit) return;

    if (mode === 'searching') {
      character.root.scale.setScalar(AR_BASE_SCALE);
      scene.add(character.root);
      if (shadowCatcher) {
        shadowCatcher.visible = true;
        scene.add(shadowCatcher);
      }
    }
    character.root.position.set(hit.position.x, hit.position.y, hit.position.z);
    syncShadowCatcher();
    setMode('placed');
    if (reticle) reticle.visible = false;
  }

  function reset() {
    if (character?.root.parent) {
      character.root.parent.remove(character.root);
    }
    if (character) {
      character.root.scale.setScalar(AR_BASE_SCALE);
      character.root.rotation.set(0, 0, 0);
    }
    if (shadowCatcher) {
      shadowCatcher.visible = false;
      shadowCatcher.parent?.remove(shadowCatcher);
    }
    setMode('searching');
  }

  function onTouchStart(e: TouchEvent) {
    if (e.touches.length === 1) {
      const t = e.touches[0]!;
      tapStart = { x: t.clientX, y: t.clientY, t: performance.now() };
      touchKind = 'tap';
      if (mode === 'placed' && character) {
        dragStartRotY = character.root.rotation.y;
      }
    } else if (e.touches.length === 2) {
      const dx = e.touches[0]!.clientX - e.touches[1]!.clientX;
      const dy = e.touches[0]!.clientY - e.touches[1]!.clientY;
      pinchStartDist = Math.hypot(dx, dy);
      pinchStartScale = character?.root.scale.x ?? AR_BASE_SCALE;
      touchKind = 'pinch';
    }
  }

  function onTouchMove(e: TouchEvent) {
    if (touchKind === 'pinch' && e.touches.length === 2 && character) {
      const dx = e.touches[0]!.clientX - e.touches[1]!.clientX;
      const dy = e.touches[0]!.clientY - e.touches[1]!.clientY;
      const dist = Math.hypot(dx, dy);
      const scale = pinchStartScale * (dist / Math.max(1, pinchStartDist));
      const clamped = Math.max(AR_SCALE_MIN, Math.min(AR_SCALE_MAX, scale));
      character.root.scale.setScalar(clamped);
      e.preventDefault();
      return;
    }
    if (touchKind === 'tap' && e.touches.length === 1) {
      const t = e.touches[0]!;
      const dx = t.clientX - tapStart.x;
      const dy = t.clientY - tapStart.y;
      if (Math.abs(dx) > TAP_MOVE_THRESHOLD || Math.abs(dy) > TAP_MOVE_THRESHOLD) {
        touchKind = 'drag';
      }
    }
    if (touchKind === 'drag' && e.touches.length === 1 && mode === 'placed' && character) {
      const t = e.touches[0]!;
      const dx = t.clientX - tapStart.x;
      character.root.rotation.y = dragStartRotY + dx * 0.01;
      e.preventDefault();
    }
  }

  function onTouchEnd(e: TouchEvent) {
    if (touchKind === 'tap') {
      const now = performance.now();
      const dx = tapStart.x - lastTapPos.x;
      const dy = tapStart.y - lastTapPos.y;
      const dist = Math.hypot(dx, dy);
      const isDoubleTap =
        now - lastTapTime < DOUBLE_TAP_INTERVAL_MS && dist < DOUBLE_TAP_DIST_PX;

      placeCharacterAt(tapStart.x, tapStart.y);

      if (isDoubleTap && character) {
        const next = character.cycleAnimation();
        console.log('[ar-pipeline] cycle animation →', next);
        lastTapTime = 0; // 三回目以降のタップを誤判定しない
      } else {
        lastTapTime = now;
        lastTapPos = { x: tapStart.x, y: tapStart.y };
      }
    }
    if (e.touches.length === 0) {
      touchKind = 'none';
    } else if (e.touches.length === 1) {
      // ピンチ解除後の片手継続
      touchKind = 'none';
    }
  }

  function bindTouch(c: HTMLCanvasElement) {
    c.addEventListener('touchstart', onTouchStart, { passive: false });
    c.addEventListener('touchmove', onTouchMove, { passive: false });
    c.addEventListener('touchend', onTouchEnd, { passive: false });
    c.addEventListener('touchcancel', onTouchEnd, { passive: false });
  }

  function unbindTouch(c: HTMLCanvasElement) {
    c.removeEventListener('touchstart', onTouchStart);
    c.removeEventListener('touchmove', onTouchMove);
    c.removeEventListener('touchend', onTouchEnd);
    c.removeEventListener('touchcancel', onTouchEnd);
  }

  return {
    module: {
      name: 'ms-character-placement',
      onStart: async ({ canvas: c }) => {
        canvas = c;
        const xrScene = xr().Threejs.xrScene();
        scene = xrScene.scene as THREE.Scene;
        const cam = xrScene.camera as THREE.PerspectiveCamera;
        const renderer = xrScene.renderer as THREE.WebGLRenderer | undefined;

        // 公式 example に倣い、XrController に three.js カメラを同期
        xr().XrController.updateCameraProjectionMatrix({
          origin: cam.position,
          facing: cam.quaternion,
        });

        // シャドウマップ有効化（地面に shadow catcher 経由で影を落とすため）
        if (renderer) {
          renderer.shadowMap.enabled = true;
          renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        } else {
          console.warn('[ar-pipeline] xrScene.renderer not available; shadows disabled');
        }

        // ライト
        scene.add(new THREE.AmbientLight(0xffffff, 0.7));
        const dir = new THREE.DirectionalLight(0xffffff, 0.8);
        dir.position.set(2, 10, 3);
        dir.castShadow = !!renderer;
        // 影カメラの正射影範囲（キャラ周辺 ±1.5m を覆えば十分）
        dir.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
        dir.shadow.camera.near = 0.1;
        dir.shadow.camera.far = 20;
        dir.shadow.camera.left = -SHADOW_CAMERA_HALF;
        dir.shadow.camera.right = SHADOW_CAMERA_HALF;
        dir.shadow.camera.top = SHADOW_CAMERA_HALF;
        dir.shadow.camera.bottom = -SHADOW_CAMERA_HALF;
        dir.shadow.bias = -0.0005;
        scene.add(dir);
        // ライトターゲットをシーンに足し、placement 後にキャラへ追従させる
        scene.add(dir.target);
        dirLight = dir;

        // 影だけを受け取る透明プレーン（shadow catcher）
        // - ShadowMaterial は影部分以外を完全透明にする
        // - depthWrite=false にしておくと半透明な顔と重なっても破綻しない
        const catcherGeo = new THREE.PlaneGeometry(SHADOW_CATCHER_SIZE, SHADOW_CATCHER_SIZE);
        const catcherMat = new THREE.ShadowMaterial({
          opacity: SHADOW_OPACITY,
          transparent: true,
          depthWrite: false,
        });
        const catcher = new THREE.Mesh(catcherGeo, catcherMat);
        catcher.rotation.x = -Math.PI / 2;
        catcher.receiveShadow = true;
        catcher.visible = false;
        // placement までシーン外に置いておく
        shadowCatcher = catcher;

        // レチクル（白い半透明リング、平面に水平に置く）
        const ringGeo = new THREE.RingGeometry(0.08, 0.1, 32);
        const ringMat = new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.7,
          side: THREE.DoubleSide,
          depthWrite: false,
        });
        reticle = new THREE.Mesh(ringGeo, ringMat);
        reticle.rotation.x = -Math.PI / 2;
        reticle.visible = false;
        scene.add(reticle);

        // GLB ロード（シーンには配置時に追加）
        try {
          character = await buildCharacter(opts.params);
          character.root.scale.setScalar(AR_BASE_SCALE);
          console.log('[ar-pipeline] character ready');
        } catch (err) {
          console.error('[ar-pipeline] character build failed', err);
        }

        bindTouch(c);
        clock.start();
      },
      onUpdate: () => {
        const dt = clock.getDelta();
        character?.mixer?.update(dt);

        if (mode === 'searching' && reticle) {
          frameCount++;
          // 画面中央でレチクル位置を更新
          const hit = hitAt(0.5, 0.5);
          if (hit) {
            hitCount++;
            reticle.visible = true;
            reticle.position.set(hit.position.x, hit.position.y, hit.position.z);
          } else {
            reticle.visible = false;
          }
          // 60 フレームごとに統計をログ
          if (frameCount % 60 === 0) {
            console.log('[ar-pipeline] hit-test stats', {
              frames: frameCount,
              hits: hitCount,
              ratio: (hitCount / frameCount).toFixed(2),
              lastHit: hit ? 'yes' : 'no',
            });
          }
        }
      },
      onDetach: () => {
        if (canvas) unbindTouch(canvas);
        character?.dispose();
        if (reticle) {
          reticle.geometry.dispose();
          (reticle.material as THREE.Material).dispose();
        }
        if (shadowCatcher) {
          shadowCatcher.parent?.remove(shadowCatcher);
          shadowCatcher.geometry.dispose();
          (shadowCatcher.material as THREE.Material).dispose();
          shadowCatcher = null;
        }
        if (dirLight) {
          dirLight.parent?.remove(dirLight);
          dirLight.target.parent?.remove(dirLight.target);
          dirLight.dispose();
          dirLight = null;
        }
      },
    },
    handles: {
      reset,
      getMode: () => mode,
    },
  };
}
